import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

const PARAMS = ['response_type', 'client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'scope', 'state', 'resource'] as const
type ConsentSearch = Partial<Record<(typeof PARAMS)[number], string>>

const SCOPE_LABELS: Record<string, string> = {
  'monitors:read': 'See your monitors',
  'monitors:write': 'Create, change, pause and delete monitors',
  'incidents:read': 'See incidents',
  'incidents:write': 'Acknowledge incidents',
  'status_pages:read': 'See status pages',
  'alerts:read': 'See alert channels',
  'alerts:write': 'Create alert channels',
  'analytics:read': 'See uptime and response-time data',
}

export const Route = createFileRoute('/oauth/consent')({
  validateSearch: (search: Record<string, unknown>): ConsentSearch =>
    Object.fromEntries(PARAMS.flatMap((k) => (typeof search[k] === 'string' ? [[k, search[k] as string]] : []))),
  component: ConsentPage,
  head: () => ({ meta: [{ title: 'Allow access — Uptrack' }, { name: 'robots', content: 'noindex' }] }),
})

function hostOf(url: string | undefined): string {
  try {
    return url ? new URL(url).host : 'unknown'
  } catch {
    return 'unknown'
  }
}

// OAuth consent for MCP clients (openspec: bug hunt 2026-09-27). The server
// no longer auto-approves self-registered clients; a code is issued only when
// the signed-in user approves here. We show where the code goes (the
// redirect host, validated by the server) — never a client-supplied name.
function ConsentPage() {
  const search = Route.useSearch()
  const [pending, setPending] = useState<'approve' | 'deny' | null>(null)
  const scopes = (search.scope?.split(/\s+/).filter(Boolean) ?? Object.keys(SCOPE_LABELS))
  const host = hostOf(search.redirect_uri)
  // Without a client and a redirect there is nothing to allow. The server would refuse it too.
  const invalid = !search.client_id || !search.redirect_uri

  async function decide(approve: boolean) {
    if (approve && invalid) return
    setPending(approve ? 'approve' : 'deny')
    try {
      const res = await fetch(`${API_URL}/oauth/authorize/decision`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...search, approve }),
      })
      const body = (await res.json().catch(() => ({}))) as { redirect_to?: string; error?: string; error_description?: string }
      if (res.status === 401) {
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`
        return
      }
      if (!res.ok || !body.redirect_to) {
        toast.error(body.error_description || body.error || 'This request is not valid. Start the connection again.')
        setPending(null)
        return
      }
      window.location.href = body.redirect_to
    } catch {
      toast.error('Could not reach Uptrack. Try again.')
      setPending(null)
    }
  }

  return (
    <main id="main-content" className="flex min-h-screen items-start justify-center px-4 py-20">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 data-slot="card-title" className="text-2xl leading-none font-semibold">
            Allow access to your Uptrack organization?
          </h1>
          <CardDescription>
            An application at <span className="font-medium text-foreground">{host}</span> asks for access through the Uptrack MCP connector.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {invalid && (
            <p role="alert" className="rounded-md border border-status-down bg-status-down-soft p-3 text-sm text-status-down">
              This request is not valid. It has no client or no return address. Start the connection again from the application.
            </p>
          )}
          <div>
            <p className="mb-2 text-sm font-medium">It will be able to:</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {scopes.map((s) => <li key={s}>{SCOPE_LABELS[s] ?? s}</li>)}
            </ul>
          </div>
          <p className="flex items-start gap-2 rounded-md border border-status-degraded bg-status-degraded-soft p-3 text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-degraded" />
            Only allow this if you started this connection yourself, for example in Claude or another MCP client.
          </p>
        </CardContent>
        <CardFooter className="flex gap-3">
          <Button variant="outline" className="flex-1" disabled={pending !== null} onClick={() => decide(false)}>
            {pending === 'deny' ? 'Denying...' : 'Deny'}
          </Button>
          <Button className="flex-1" disabled={pending !== null || invalid} onClick={() => decide(true)}>
            {pending === 'approve' ? 'Allowing...' : 'Allow'}
          </Button>
        </CardFooter>
      </Card>
    </main>
  )
}
